import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, Trash2, ExternalLink, Plus, X, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';
import AssetAssignmentModal from './AssetAssignmentModal';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Modal that shows the list of assets currently assigned to a target user,
 * with a button to add more and per-row unassign. superadmin only — UI gated
 * by the parent UsersPage.
 */
const UserAssignedAssetsModal = ({ open, onClose, user }) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [globalView, setGlobalView] = useState(false);
  const [info, setInfo] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [unassigningId, setUnassigningId] = useState(null);

  const fetchItems = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/users/${user.id}/assigned-assets?limit=200`, { withCredentials: true });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
      setGlobalView(!!r.data?.global_view);
      setInfo(r.data?.info || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar asignaciones');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (open) fetchItems();
  }, [open, fetchItems]);

  const handleUnassign = async (assetId) => {
    if (!window.confirm('¿Quitar la asignación de este activo?')) return;
    setUnassigningId(assetId);
    try {
      await axios.delete(`${API}/admin/users/${user.id}/assign-assets/${assetId}`, { withCredentials: true });
      toast.success('Asignación retirada');
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al desasignar');
    } finally {
      setUnassigningId(null);
    }
  };

  if (!open) return null;
  const targetLabel = user ? `${user.name || user.email} · ${user.role}` : '';

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center p-4"
      style={{ background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
      data-testid="user-assigned-modal"
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">Activos asignados</h3>
              <p className="text-xs text-slate-500 mt-0.5">de <strong>{targetLabel}</strong></p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!globalView && (
              <button
                onClick={() => setShowAdd(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
                data-testid="user-add-assets-btn"
              >
                <Plus className="w-3.5 h-3.5" /> Añadir activo
              </button>
            )}
            <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded" data-testid="user-assigned-modal-close">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </div>
        </div>

        {globalView ? (
          <div className="p-10 text-center">
            <Info className="w-10 h-10 mx-auto mb-3 text-blue-500" />
            <p className="text-sm text-slate-700 max-w-md mx-auto">{info}</p>
          </div>
        ) : (
          <>
            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-10 text-center text-slate-400 flex flex-col items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <p className="text-sm">Cargando…</p>
                </div>
              ) : items.length === 0 ? (
                <div className="p-10 text-center" data-testid="user-assigned-empty">
                  <Building2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm text-slate-500 mb-1">Este usuario no tiene activos asignados</p>
                  <p className="text-xs text-slate-400">Usa "Añadir activo" para asignar uno o varios.</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 sticky top-0">
                    <tr className="border-b border-slate-200">
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">NPL</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Dirección</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Provincia</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Municipio</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Fase</th>
                      <th className="text-left px-3 py-2 text-xs font-medium text-slate-500 uppercase">Estado</th>
                      <th className="text-right px-3 py-2 text-xs font-medium text-slate-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map(a => (
                      <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/60" data-testid={`assigned-row-${a.id}`}>
                        <td className="px-3 py-2 font-mono text-xs text-slate-600">{a.codigo_npl || a.id.slice(0, 8) + '…'}</td>
                        <td className="px-3 py-2 truncate max-w-[200px] text-slate-700">{a.direccion || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{a.provincia || '-'}</td>
                        <td className="px-3 py-2 text-slate-600">{a.municipio || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{a.fase || '-'}</td>
                        <td className="px-3 py-2 text-xs text-slate-500">{a.estado || '-'}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Link
                              to={`/activos/${a.id}`}
                              target="_blank"
                              rel="noopener"
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-600 hover:bg-blue-50"
                              data-testid={`assigned-view-${a.id}`}
                            >
                              <ExternalLink className="w-3 h-3" /> Ver
                            </Link>
                            <button
                              onClick={() => handleUnassign(a.id)}
                              disabled={unassigningId === a.id}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-red-600 hover:bg-red-50"
                              data-testid={`assigned-remove-${a.id}`}
                            >
                              {unassigningId === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                              Quitar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t border-slate-100 text-xs text-slate-500 text-center">
              {total} activo{total === 1 ? '' : 's'} asignado{total === 1 ? '' : 's'}
            </div>
          </>
        )}
      </div>

      <AssetAssignmentModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        targetUser={user}
        excludeAssetIds={items.map(a => a.id)}
        onAssigned={() => fetchItems()}
      />
    </div>
  );
};

export default UserAssignedAssetsModal;
